/**
 * Aides au matching « responsable de dossier » ↔ membre de l'organisation.
 *
 * Le cloisonnement repose sur des alias (valeurs exactes de responsable_dossier,
 * accents compris) assignés à un membre. Pour faire gagner du temps à l'admin,
 * on pré-suggère les responsables dont le nom ressemble à celui du membre :
 * chevauchement de tokens, insensible à la casse ET aux accents.
 *
 * Important : on ne fait QUE suggérer. L'admin confirme / ajuste toujours.
 */

/**
 * Normalise une chaîne pour comparaison : minuscules, accents retirés,
 * ponctuation transformée en espaces. Ex : "Élodie  L'Hôte-Martin" ->
 * "elodie l hote martin".
 */
// Plage U+0300..U+036F = "Combining Diacritical Marks". Construite via escapes
// ASCII (new RegExp) pour eviter tout caractere combinant dans ce fichier source.
const DIACRITICS_REGEX = new RegExp('[\\u0300-\\u036f]', 'g');

export function normalizeForMatch(value: string | null | undefined): string {
  if (!value) return '';
  return (
    value
      .normalize('NFD')
      // Retirer les diacritiques (accents) une fois la chaine decomposee
      .replace(DIACRITICS_REGEX, '')
      .toLowerCase()
      // Tout ce qui n'est pas lettre/chiffre devient un separateur
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
  );
}

/**
 * Découpe une chaîne en tokens normalisés (en ignorant les tokens d'une seule
 * lettre, peu discriminants : initiales, "l'", "d'"...).
 */
export function tokenize(value: string | null | undefined): string[] {
  const normalized = normalizeForMatch(value);
  if (!normalized) return [];
  return normalized.split(' ').filter(token => token.length > 1);
}

/**
 * Parmi une liste de noms de responsables, renvoie ceux qui « ressemblent » au
 * nom du membre (chevauchement de tokens). Utilisé pour pré-cocher / suggérer
 * les alias dans l'UI admin.
 *
 * @param memberName  Nom complet du membre (ex. "Prénom Nom").
 * @param responsables Noms de responsables candidats (valeurs exactes).
 * @returns Le sous-ensemble de `responsables` qui chevauche le nom du membre.
 */
export function suggestResponsablesForName(
  memberName: string | null | undefined,
  responsables: string[]
): string[] {
  const memberTokens = tokenize(memberName);
  if (memberTokens.length === 0) return [];
  const memberSet = new Set(memberTokens);
  return responsables.filter(responsable =>
    tokenize(responsable).some(token => memberSet.has(token))
  );
}
